import {
  CertificateRecord,
  LogReplay,
  PublicEvent,
  REGISTRATION_FIELDS,
  RegistrationRecordEntry,
  compareSequence,
  currentRegistration,
  WrpStatus,
  wrpStatus,
} from "./transparency-log";
import { readCredentials } from "./requested-data";
import { WatchFinding } from "./watch-rules";

export type WatchView = "findings" | "register" | "requests" | "activity";

export interface CertificateCount {
  valid: number;
  total: number;
}

export interface WrpRow {
  wrpId: string;
  name: string;
  status: WrpStatus;
  /** The current registration record; it states the status if it can. */
  currentRecord?: RegistrationRecordEntry;
  access: CertificateCount;
  registration: CertificateCount;
  high: number;
  warning: number;
  lastEvent: PublicEvent;
  certificates: CertificateRecord[];
  /** Registration records, newest first. */
  registrationRecords: RegistrationRecordEntry[];
}

/** Everything the views need to link back to the public API. */
export interface WatchContext {
  api: string;
  /** The log position the register was compared at. */
  asOfSequence: string;
  /** evaluated_at of the compared register state, when it could be read. */
  evaluatedAt?: string;
  names: Map<string, string>;
  /** Certificates by the event id of their issuance and revocation. */
  certificates: Map<string, CertificateRecord>;
  /** Certificates by fingerprint. */
  records: Map<string, CertificateRecord>;
  /** Registration records by the event id that published them. */
  registrations: Map<string, RegistrationRecordEntry>;
}

export function buildWrpRows(
  replayed: LogReplay,
  findings: WatchFinding[],
  names: Map<string, string>,
): WrpRow[] {
  const byWrp = new Map<string, CertificateRecord[]>();
  for (const record of replayed.certificates.values()) {
    const list = byWrp.get(record.wrpId) ?? [];
    list.push(record);
    byWrp.set(record.wrpId, list);
  }

  // A WRP is part of the register with a certificate or a record.
  return [...new Set([...byWrp.keys(), ...replayed.registrations.keys()])]
    .map((wrpId) => {
      const certificates = (byWrp.get(wrpId) ?? []).sort((left, right) =>
        compareSequence(
          right.issued.sequence_number,
          left.issued.sequence_number,
        ),
      );
      const registrations = replayed.registrations.get(wrpId) ?? [];
      const lastEvent = [
        ...certificates.flatMap((record) =>
          record.revoked ? [record.issued, record.revoked] : [record.issued],
        ),
        ...registrations.map((entry) => entry.event),
      ].reduce((latest, event) =>
        compareSequence(event.sequence_number, latest.sequence_number) > 0
          ? event
          : latest,
      );
      const wrpFindings = findings.filter((finding) => finding.wrpId === wrpId);
      return {
        wrpId,
        name: names.get(wrpId) ?? wrpId,
        status: wrpStatus(certificates, registrations),
        currentRecord: currentRegistration(registrations),
        access: count(certificates, "access_certificate"),
        registration: count(certificates, "registration_certificate"),
        high: wrpFindings.filter((finding) => finding.severity === "high")
          .length,
        warning: wrpFindings.filter((finding) => finding.severity === "warning")
          .length,
        lastEvent,
        certificates,
        registrationRecords: [...registrations].reverse(),
      };
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

/** Maps issuance and revocation event ids to the certificate they concern. */
export function certificatesByEvent(
  replayed: LogReplay,
): Map<string, CertificateRecord> {
  const lookup = new Map<string, CertificateRecord>();
  for (const record of replayed.certificates.values()) {
    lookup.set(record.issued.event_id, record);
    if (record.revoked) {
      lookup.set(record.revoked.event_id, record);
    }
  }
  return lookup;
}

/** Maps registration_recorded event ids to their record. */
export function registrationsByEvent(
  replayed: LogReplay,
): Map<string, RegistrationRecordEntry> {
  const lookup = new Map<string, RegistrationRecordEntry>();
  for (const entries of replayed.registrations.values()) {
    for (const entry of entries) {
      lookup.set(entry.event.event_id, entry);
    }
  }
  return lookup;
}

export function certificateLabel(record?: CertificateRecord): string {
  if (record?.type === "registration_certificate") {
    return "Registration certificate";
  }
  if (record?.type === "access_certificate") {
    return "Access certificate";
  }
  return "Certificate";
}

export function subjectName(record?: CertificateRecord): string | undefined {
  return record?.registration?.name ?? record?.access?.commonName;
}

/** What an event did to the certificate or registration it concerns. */
export function eventAction(event: PublicEvent): string {
  switch (event.event_type) {
    case "certificate_issued":
      return "issued";
    case "certificate_revoked":
      return "revoked";
    case "registration_recorded":
      return "recorded";
    default:
      // A type the monitor does not know (reported by R-01).
      return `of type ${String(event.event_type ?? "none")}`;
  }
}

/** The status a registration record states, or why it states none. */
export function registrationStatusLabel(
  entry?: RegistrationRecordEntry,
): string {
  if (!entry) return "no record published";
  if (entry.parseError) return "unreadable record";
  return entry.status ?? "no valid registration status";
}

/** One evidence or activity line: what happened to what, and why. */
export function describeEvent(context: WatchContext, event: PublicEvent) {
  const record = context.certificates.get(event.event_id);
  const registration = context.registrations.get(event.event_id);
  const recorded = event.event_type === "registration_recorded";
  const print = record?.fingerprint ?? registration?.fingerprint;
  return {
    record,
    registration,
    label: recorded
      ? "Registration"
      : event.event_type === "certificate_issued" ||
          event.event_type === "certificate_revoked"
        ? certificateLabel(record)
        : "Event",
    action: eventAction(event),
    subject: recorded ? recordName(registration) : subjectName(record),
    fingerprint: print ? shortFingerprint(print) : undefined,
    reason:
      event.event_type === "certificate_revoked"
        ? revocationReasonLabel(event)
        : undefined,
    status: recorded ? registrationStatusLabel(registration) : undefined,
  };
}

/** The display_name a registration record states. */
export function recordName(
  entry?: RegistrationRecordEntry,
): string | undefined {
  const name = entry?.payload?.display_name;
  return typeof name === "string" && name ? name : undefined;
}

export function shortFingerprint(fingerprint: string): string {
  const hex = fingerprint.replace(/^sha-256:/, "");
  return `${hex.slice(0, 8)}…${hex.slice(-6)}`;
}

export function eventLink(context: WatchContext, event: PublicEvent): string {
  return `${context.api}/certificate-events/${encodeURIComponent(event.event_id)}`;
}

export function wrpLink(context: WatchContext, wrpId: string): string {
  return `${context.api}/wrps/${encodeURIComponent(wrpId)}?${snapshotQuery(context)}`;
}

export function intendedUseLink(context: WatchContext, id: string): string {
  return `${context.api}/intended-uses/${encodeURIComponent(id)}?${snapshotQuery(context)}`;
}

/** Register search for intended uses requesting an attestation type. */
export function attestationTypeLink(
  context: WatchContext,
  type: string,
  formats: string[],
): string {
  const query = snapshotQuery(context);
  query.set(formats.includes("mso_mdoc") ? "doctype" : "vct", type);
  return `${context.api}/intended-uses?${query}`;
}

/** Selects the compared register state, so a link shows what was compared. */
function snapshotQuery(context: WatchContext): URLSearchParams {
  const query = new URLSearchParams({ as_of_sequence: context.asOfSequence });
  if (context.evaluatedAt) query.set("evaluated_at", context.evaluatedAt);
  return query;
}

/** In-app link: views and their selection live in the URL fragment. */
export function viewLink(view: WatchView, key?: string): string {
  return key ? `#${view}/${encodeURIComponent(key)}` : `#${view}`;
}

export interface FieldChange {
  field: string;
  before?: string;
  after?: string;
}

/** Fields that differ on every issuance and say nothing about the content. */
const PER_ISSUANCE_FIELDS = new Set([
  "iat",
  "exp",
  "jti",
  "status",
  "serialNumber",
  "notBefore",
  "notAfter",
]);

const changeCache = new WeakMap<CertificateRecord, FieldChange[]>();

/** Content differences from the certificate this one replaced. */
export function certificateChanges(
  previous: CertificateRecord,
  next: CertificateRecord,
): FieldChange[] {
  const cached = changeCache.get(next);
  if (cached) return cached;
  const before: Record<string, unknown> = {
    ...(previous.registration ?? previous.access),
  };
  const after: Record<string, unknown> = {
    ...(next.registration ?? next.access),
  };
  const changes = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((field) => !PER_ISSUANCE_FIELDS.has(field))
    .filter(
      (field) =>
        JSON.stringify(before[field] ?? null) !==
        JSON.stringify(after[field] ?? null),
    )
    .sort()
    .map((field) => ({
      field,
      before: displayValue(before[field]),
      after: displayValue(after[field]),
    }));
  changeCache.set(next, changes);
  return changes;
}

export function displayValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  return typeof value === "string" ? value : JSON.stringify(value, null, 1);
}

/** Payload fields the registrar does not read, e.g. from an older profile. */
export function unknownFields(record: CertificateRecord): string[] {
  return Object.keys(record.registration ?? {}).filter(
    (field) => !REGISTRATION_FIELDS.has(field),
  );
}

/** Last segment of an entitlement URI, e.g. "Service Provider". */
export function entitlementLabel(entitlement: string): string {
  return (entitlement.split(/[/#]/).pop() || entitlement).replace(/_/g, " ");
}

export function matchesText(
  needle: string,
  ...values: Array<string | undefined>
) {
  const query = needle.trim().toLowerCase();
  return !query || values.some((value) => value?.toLowerCase().includes(query));
}

function count(
  records: CertificateRecord[],
  type: CertificateRecord["type"],
): CertificateCount {
  const ofType = records.filter((record) => record.type === type);
  return {
    valid: ofType.filter((record) => !record.revoked).length,
    total: ofType.length,
  };
}

/** Readable labels for revocation_reason (see the registrar's discovery document). */
export const REVOCATION_REASON_LABELS: Record<string, string> = {
  unspecified: "no reason given",
  key_compromise: "key compromise",
  cessation_of_operation: "no longer used",
  superseded: "replaced after data change",
  registration_suspended: "registration suspended",
  registration_cancelled: "registration cancelled",
};

export function revocationReasonLabel(event?: PublicEvent): string {
  const reason = event?.revocation_reason ?? "unspecified";
  return REVOCATION_REASON_LABELS[reason] ?? reason;
}

export const SIGNATURE_LABELS: Record<CertificateRecord["signature"], string> =
  {
    valid: "signature verified",
    invalid: "signature invalid",
    unknown_signer: "unknown signer",
    wrong_key_usage: "signer not a statement signer",
    unsupported: "algorithm not accepted",
    unchecked: "signature not checked",
  };

/** One line on what a registration certificate requests. */
export function requestSummary(record: CertificateRecord): string | undefined {
  if (!record.registration) return undefined;
  const credentials = readCredentials(record.registration.credentials);
  if (!credentials.length) return "Requests no credentials";
  const types = [
    ...new Set(credentials.flatMap((credential) => credential.types)),
  ];
  const claims = new Set(
    credentials.flatMap((credential) =>
      (credential.claims ?? []).map((claim) => claim.normalizedPath),
    ),
  ).size;
  const shown = types.slice(0, 2).join(", ") || "no attestation type";
  const more = types.length > 2 ? ` +${types.length - 2}` : "";
  return `Requests ${shown}${more} · ${claims ? `${claims} claims` : "no claims declared"}`;
}

/** "Replaces #12 · changed support_uri" or "Replaced by #15". */
export function replacementNote(
  context: WatchContext,
  record: CertificateRecord,
): string | undefined {
  const previous = record.replaces
    ? context.records.get(record.replaces)
    : undefined;
  if (previous) {
    const changes = certificateChanges(previous, record);
    return `Replaces #${previous.issued.sequence_number} · ${
      changes.length
        ? `changed ${changes.map((change) => change.field).join(", ")}`
        : "same content"
    }`;
  }
  const next = record.replacedBy
    ? context.records.get(record.replacedBy)
    : undefined;
  return next ? `Replaced by #${next.issued.sequence_number}` : undefined;
}
